import React, {
  useEffect,
  useState,
  MouseEvent,
  PropsWithChildren,
  useCallback,
  useMemo,
} from 'react';
import { Formik, Form, useFormikContext, useField } from 'formik';
import * as Yup from 'yup';
import { gql } from '@yi/core';
import { i18n_enter_your_email_address } from '@yi/i18n/phrases/i18n_enter_your_email_address';

import { useEmailSchema, navigate, analytics, useStorage } from '~/common';
import { Button } from '~/src/components/buttons';
import { EmailInput as BaseEmailInput } from '~/src/components/FormikInputs/EmailInput';
import type { EmailValidationStatus } from '~/src/components/FormikInputs/EmailInput';
import { useIsSSR } from '~/src/components/hooks';
import { useLocale } from '~/src/components/LocaleProvider';

interface FormValues {
  email: string;
}

const initialValues: FormValues = {
  email: '',
};

interface Status {
  emailValidationStatus: EmailValidationStatus;
  serverErrors: Partial<gql.typesClient.Mutate_RegisterUser_Base_registerUser_errors> | null;
}
const initialStatus: Status = {
  emailValidationStatus: 'idle',
  serverErrors: null,
};

const SubmitButton: React.FC<
  PropsWithChildren<{ className?: string } & Omit<React.ComponentProps<typeof Button>, 'onClick'>>
> = ({ children, disabled = false, showSpinner = false, ...props }) => {
  const { status, isSubmitting, isValid, validateField, submitForm } = useFormikContext();
  const [isWaitingEmailValidation, setIsWaitingEmailValidation] = useState(false);
  const [, , { setTouched }] = useField('email');

  const onClick = (e: MouseEvent) => {
    e.preventDefault();
    if (!isSubmitting && isValid && status?.emailValidationStatus === 'valid') {
      submitForm();
    } else {
      setTouched(true);
      validateField('email');
      setIsWaitingEmailValidation(true);
    }
  };

  useEffect(() => {
    if (isWaitingEmailValidation) {
      if (!isSubmitting && isValid && status?.emailValidationStatus === 'valid') {
        setIsWaitingEmailValidation(false);
        submitForm();
      } else if (status?.emailValidationStatus === 'invalid') {
        setIsWaitingEmailValidation(false);
      } else if (status?.emailValidationStatus === 'idle' && !isValid) {
        setIsWaitingEmailValidation(false);
      }
    }
  }, [isSubmitting, isValid, isWaitingEmailValidation, status?.emailValidationStatus, submitForm]);

  const isSSR = useIsSSR();

  const btnProps = {
    disabled:
      isSSR ||
      isSubmitting ||
      !isValid ||
      status?.emailValidationStatus === 'invalid' ||
      disabled ||
      isWaitingEmailValidation,
    showSpinner: isSSR || isSubmitting || isWaitingEmailValidation || showSpinner,
    onClick,
  };

  return (
    <Button {...btnProps} {...props}>
      {children}
    </Button>
  );
};

const EmailInput: React.FC<
  Pick<React.ComponentProps<typeof BaseEmailInput>, 'label' | 'variant' | 'fullWidth'> & {
    className?: string;
  }
> = ({ label, ...props }) => {
  const { locale } = useLocale();
  return (
    <BaseEmailInput
      label={label ? label : i18n_enter_your_email_address(locale)}
      required
      variant="filled"
      fullWidth
      fieldName="email"
      {...props}
    />
  );
};

export const EnterYourEmailForm: React.FC<
  PropsWithChildren<{ className?: string; onSubmit?: (params: { email: string }) => void }>
> & {
  SubmitButton: typeof SubmitButton;
  Input: typeof EmailInput;
} = ({ children, onSubmit, ...props }) => {
  const returnUrl = '/membership';

  const storage = useStorage();

  const emailSchema = useEmailSchema();

  const validationSchema: Yup.SchemaOf<FormValues> = Yup.object({
    email: emailSchema,
  });

  const defaultOnSubmit = useCallback(() => {
    navigate(returnUrl);
  }, []);

  const handleSubmit = useMemo(() => {
    if (typeof onSubmit === 'function') {
      return onSubmit;
    }
    return defaultOnSubmit;
  }, [defaultOnSubmit, onSubmit]);

  return (
    <Formik<FormValues>
      initialValues={initialValues}
      initialStatus={initialStatus}
      validationSchema={validationSchema}
      onSubmit={async values => {
        const email = values.email;
        analytics.sendEmailCaptureEvent(email);
        storage.setEmail(email);
        handleSubmit({ email });
      }}
    >
      <Form {...props}>{children}</Form>
    </Formik>
  );
};

EnterYourEmailForm.SubmitButton = SubmitButton;
EnterYourEmailForm.Input = EmailInput;
